import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
} from 'reactstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApiContext } from '../../contexts/ApiContext';
import MirandaService from '../../service/miranda.service';
import { useNotification } from '../../contexts/NotificationContext';

// Página Miranda AI: relatórios diários + histórico de conversas
const conversationStorageKey = (accountId) => `miranda-ai-conversation:${accountId}`;

function formatReportDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR');
}

function renderInline(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) {
      parts.push(text.slice(last, match.index));
    }
    const token = match[0];
    if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else {
      parts.push(
        <code key={key++} style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: 4 }}>
          {token.slice(1, -1)}
        </code>,
      );
    }
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MarkdownView({ content }) {
  const blocks = useMemo(() => {
    const lines = (content || '').split('\n');
    const nodes = [];
    let list = null;

    const flushList = () => {
      if (list) {
        nodes.push(
          <ul key={`ul-${nodes.length}`} style={{ paddingLeft: 20, marginBottom: 12 }}>
            {list.map((item, i) => (
              <li key={i} style={{ marginBottom: 4 }}>{renderInline(item)}</li>
            ))}
          </ul>,
        );
        list = null;
      }
    };

    lines.forEach((raw) => {
      const line = raw.trimEnd();
      if (!line.trim()) {
        flushList();
        return;
      }
      if (line.startsWith('### ')) {
        flushList();
        nodes.push(<h5 key={`h3-${nodes.length}`} style={{ marginTop: 18 }}>{renderInline(line.slice(4))}</h5>);
        return;
      }
      if (line.startsWith('## ')) {
        flushList();
        nodes.push(<h4 key={`h2-${nodes.length}`} style={{ marginTop: 20 }}>{renderInline(line.slice(3))}</h4>);
        return;
      }
      if (line.startsWith('# ')) {
        flushList();
        nodes.push(<h3 key={`h1-${nodes.length}`} style={{ marginTop: 8 }}>{renderInline(line.slice(2))}</h3>);
        return;
      }
      if (/^[-*] /.test(line)) {
        if (!list) list = [];
        list.push(line.replace(/^[-*] /, ''));
        return;
      }
      flushList();
      nodes.push(
        <p key={`p-${nodes.length}`} style={{ marginBottom: 10, lineHeight: 1.55, color: '#334155' }}>
          {renderInline(line)}
        </p>,
      );
    });
    flushList();
    return nodes;
  }, [content]);

  return <div className="miranda-report-md">{blocks}</div>;
}

function listItemStyle(active) {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: active ? '1px solid rgba(124, 58, 237, 0.45)' : '1px solid rgba(15, 23, 42, 0.08)',
    background: active ? 'rgba(124, 58, 237, 0.08)' : '#fff',
    color: '#1e293b',
    borderRadius: 10,
    padding: '12px 14px',
    marginBottom: 8,
    cursor: 'pointer',
    boxShadow: active ? '0 4px 14px rgba(124, 58, 237, 0.12)' : 'none',
    transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
  };
}

function MirandaReportsPage() {
  const { accountId: accountIdParam } = useParams();
  const accountId = Number(accountIdParam);
  const [searchParams, setSearchParams] = useSearchParams();
  const { notify } = useNotification();
  const { account } = useContext(ApiContext);

  const tab = searchParams.get('tab') === 'conversas' ? 'conversas' : 'relatorios';
  const reportIdFromUrl = searchParams.get('reportId')
    ? Number(searchParams.get('reportId'))
    : null;

  const [status, setStatus] = useState(null);
  const [reports, setReports] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(reportIdFromUrl);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [generating, setGenerating] = useState(false);

  const setTab = (next) => {
    const nextParams = {};
    if (next === 'conversas') nextParams.tab = 'conversas';
    if (next === 'relatorios' && selectedReportId) nextParams.reportId = String(selectedReportId);
    setSearchParams(nextParams);
  };

  const loadList = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [st, reportList, convList] = await Promise.all([
        MirandaService.getStatus(accountId),
        MirandaService.listReports(accountId),
        MirandaService.listConversations(accountId),
      ]);
      setStatus(st);
      const reportRows = Array.isArray(reportList) ? reportList : [];
      const convRows = Array.isArray(convList) ? convList : [];
      setReports(reportRows);
      setConversations(convRows);
      setSelectedReportId((prev) => {
        if (reportIdFromUrl && reportRows.some((r) => r.id === reportIdFromUrl)) {
          return reportIdFromUrl;
        }
        return prev || reportRows[0]?.id || null;
      });
      setSelectedConvId((prev) => prev || convRows[0]?.id || null);
    } catch (err) {
      notify('tr', 'danger', 'Erro ao carregar Miranda AI', 'icon-alert-circle-exc');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [accountId, notify, reportIdFromUrl]);

  useEffect(() => {
    if (reportIdFromUrl) {
      setSelectedReportId(reportIdFromUrl);
    }
  }, [reportIdFromUrl]);

  const handleGenerateReport = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const report = await MirandaService.generateReport(accountId, 1);
      notify('tr', 'success', 'Relatório diário gerado', 'icon-check-2');
      setSearchParams({ reportId: String(report.id) });
      await loadList();
      setSelectedReportId(report.id);
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao gerar relatório',
        'icon-alert-circle-exc',
      );
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (tab !== 'relatorios' || !selectedReportId || !accountId) {
      if (tab !== 'relatorios') setSelectedReport(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    MirandaService.getReport(accountId, selectedReportId)
      .then((data) => {
        if (!cancelled) setSelectedReport(data);
      })
      .catch(() => {
        if (!cancelled) notify('tr', 'danger', 'Erro ao abrir relatório', 'icon-alert-circle-exc');
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, selectedReportId, accountId, notify]);

  useEffect(() => {
    if (tab !== 'conversas' || !selectedConvId || !accountId) {
      if (tab !== 'conversas') setSelectedConv(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    MirandaService.getConversation(accountId, selectedConvId)
      .then((data) => {
        if (!cancelled) setSelectedConv(data);
      })
      .catch(() => {
        if (!cancelled) notify('tr', 'danger', 'Erro ao abrir conversa', 'icon-alert-circle-exc');
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, selectedConvId, accountId, notify]);

  const continueConversation = () => {
    if (!selectedConvId) return;
    localStorage.setItem(conversationStorageKey(accountId), String(selectedConvId));
    notify('tr', 'success', 'Conversa pronta — abra o botão do Miranda AI', 'icon-check-2');
  };

  if (loading) {
    return (
      <div className="content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!status?.enabled) {
    return (
      <div className="content">
        <Card>
          <CardBody className="text-center py-5">
            <h3 className="mb-2">Miranda AI</h3>
            <p className="text-muted mb-0">
              O Miranda AI não está habilitado nesta conta.
              {account?.name ? ` (${account.name})` : ''}
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="content">
      <style>{`
        .miranda-page-tabs {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 1.25rem;
        }
        .miranda-page-tab {
          appearance: none;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #64748b;
          font-weight: 600;
          font-size: 13px;
          line-height: 1;
          padding: 10px 18px;
          border-radius: 999px;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .miranda-page-tab:hover {
          color: #7c3aed;
          border-color: rgba(124, 58, 237, 0.35);
          background: rgba(124, 58, 237, 0.04);
        }
        .miranda-page-tab.is-active {
          color: #fff;
          border-color: transparent;
          background: linear-gradient(135deg, #7c3aed 0%, #2563eb 100%);
          box-shadow: 0 6px 16px rgba(124, 58, 237, 0.28);
        }
      `}</style>

      <Row className="mb-3 align-items-end">
        <Col md="8">
          <h2 className="mb-1">Miranda AI</h2>
          <p className="text-muted mb-0">
            Relatórios diários e histórico de conversas
          </p>
        </Col>
        <Col md="4" className="text-md-right mt-3 mt-md-0">
          <Button
            color="primary"
            size="sm"
            className="mr-2"
            onClick={handleGenerateReport}
            disabled={generating}
          >
            {generating ? 'Gerando…' : 'Gerar relatório'}
          </Button>
          <Button color="secondary" outline size="sm" onClick={loadList}>
            Atualizar
          </Button>
        </Col>
      </Row>

      <div className="miranda-page-tabs" role="tablist" aria-label="Seções do Miranda AI">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'relatorios'}
          className={`miranda-page-tab${tab === 'relatorios' ? ' is-active' : ''}`}
          onClick={() => setTab('relatorios')}
        >
          Relatórios
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'conversas'}
          className={`miranda-page-tab${tab === 'conversas' ? ' is-active' : ''}`}
          onClick={() => setTab('conversas')}
        >
          Conversas
        </button>
      </div>

      {tab === 'relatorios' ? (
        <Row>
          <Col lg="4" className="mb-4">
            <Card style={{ height: '100%' }}>
              <CardHeader>
                <h4 className="card-title mb-0">Histórico de relatórios</h4>
              </CardHeader>
              <CardBody style={{ maxHeight: 620, overflowY: 'auto', paddingTop: 8 }}>
                {reports.length === 0 ? (
                  <p className="text-muted mb-0">
                    Ainda não há relatórios. Eles são gerados automaticamente pela manhã.
                  </p>
                ) : (
                  reports.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => {
                        setSelectedReportId(r.id);
                        setSearchParams({ reportId: String(r.id) });
                      }}
                      style={listItemStyle(r.id === selectedReportId)}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{r.title}</div>
                      <small className="text-muted">{formatReportDate(r.reportDate)}</small>
                    </button>
                  ))
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg="8" className="mb-4">
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">
                  {selectedReport?.title || 'Selecione um relatório'}
                </h4>
              </CardHeader>
              <CardBody>
                {loadingDetail ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : selectedReport ? (
                  <MarkdownView content={selectedReport.contentMd} />
                ) : (
                  <p className="text-muted mb-0">Escolha um dia na lista ao lado.</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col lg="4" className="mb-4">
            <Card style={{ height: '100%' }}>
              <CardHeader>
                <h4 className="card-title mb-0">Histórico de conversas</h4>
              </CardHeader>
              <CardBody style={{ maxHeight: 620, overflowY: 'auto', paddingTop: 8 }}>
                {conversations.length === 0 ? (
                  <p className="text-muted mb-0">
                    Nenhuma conversa ainda. Use o botão flutuante do Miranda AI para começar.
                  </p>
                ) : (
                  conversations.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedConvId(c.id)}
                      style={listItemStyle(c.id === selectedConvId)}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{c.title}</div>
                      <small className="text-muted">
                        {formatDateTime(c.updatedAt)}
                        {c._count?.messages != null ? ` · ${c._count.messages} msgs` : ''}
                      </small>
                    </button>
                  ))
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg="8" className="mb-4">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <h4 className="card-title mb-0">
                  {selectedConv?.title || 'Selecione uma conversa'}
                </h4>
                {selectedConv && (
                  <Button color="primary" size="sm" onClick={continueConversation}>
                    Continuar no chat
                  </Button>
                )}
              </CardHeader>
              <CardBody>
                {loadingDetail ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : selectedConv ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {(selectedConv.messages || []).map((m) => (
                      <div
                        key={m.id}
                        style={{
                          alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                          maxWidth: '88%',
                          padding: '10px 13px',
                          borderRadius: 14,
                          fontSize: 13.5,
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background:
                            m.role === 'user'
                              ? 'linear-gradient(135deg, #7c3aed, #2563eb)'
                              : '#f8fafc',
                          border: m.role === 'user' ? 'none' : '1px solid rgba(15, 23, 42, 0.08)',
                          color: m.role === 'user' ? '#fff' : '#1e293b',
                        }}
                      >
                        {m.content}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">Escolha uma conversa na lista ao lado.</p>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default MirandaReportsPage;
